/**
 * Data layer for the internal metrics dashboard.
 *
 * Fetches raw entries for every licensed timekeeper and folds them through the
 * pure aggregator. This is the ONLY file that knows where the numbers come
 * from — when payload size forces a server-side aggregate, replace the query
 * here and leave `aggregate.ts` and every component untouched.
 */

import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { useApi } from "@/shared/utils/request";
import { logger } from "@/shared/utils/logger";
import { useProfessionals } from "@/features/admin/hooks/useAdminData";
import { aggregate } from "./aggregate";
import { isExcludedAccount } from "./targets";
import type { MetricStatus, MetricsRow, MetricsSummary } from "./types";

function toApiDate(d: Date): string {
	const y = d.getFullYear();
	const m = String(d.getMonth() + 1).padStart(2, "0");
	const day = String(d.getDate()).padStart(2, "0");
	return `${y}-${m}-${day}`;
}

/**
 * Row shape from GET /admin/user-transactions.
 *
 * Mirrors the interface in useInternalEntries.ts, plus the two v2 provenance
 * fields. Both are optional: they are absent from responses until the backend
 * migration ships, and null on every pre-migration row afterwards.
 */
interface AdminUserTransaction {
	id: number;
	prof_id: string;
	prof_name: string;
	date: string;
	units: number;
	units_seconds?: number | null;
	original_units: number | null;
	description: string;
	original_description: string | null;
	matter_id: string | null;
	original_matter: string | null;
	task_code: string | null;
	original_task?: string | null;
	activity_code: string | null;
	original_activity?: string | null;
	exported: boolean;
	app_name: string | null;
	/**
	 * Synthesized by the endpoint's CTE, NOT a real column. The v2 facade emits
	 * 'time_entry_groups' / 'deleted_time_entry_groups'; older v1-shaped callers
	 * expected 'transactions' / 'deleted_transactions'. Matched by substring so
	 * a rename on either side cannot silently reclassify deleted work as
	 * tracked, which is exactly what an exact-equality check did here before.
	 */
	source: string;

	// v2 provenance — written on first user edit, null means never edited.
	originalEntryGroupId?: string | null;
	originalMatterId?: string | null;
}

function statusOf(r: AdminUserTransaction): MetricStatus {
	if (r.source?.includes("deleted")) return "deleted";
	if (r.exported) return "exported";
	return "tracked";
}

function toMetricsRow(r: AdminUserTransaction): MetricsRow {
	return {
		id: r.id,
		profId: r.prof_id,
		profName: r.prof_name,
		date: r.date,
		status: statusOf(r),
		application: r.app_name,
		// Null, never 0: `units` is a billing quantity on a 0.1h grid at some
		// firms, so it cannot stand in for elapsed duration. A row without one
		// is counted but adds no hours, and is reported as such.
		seconds: r.units_seconds ?? null,
		matterId: r.matter_id,
		originalMatter: r.original_matter,
		description: r.description,
		originalDescription: r.original_description,
		units: r.units,
		originalUnits: r.original_units,
		task: r.task_code,
		originalTask: r.original_task ?? null,
		activity: r.activity_code,
		originalActivity: r.original_activity ?? null,
		// Preserve undefined-vs-null: undefined means the field was absent from
		// the payload (pre-migration API), null means present but never edited.
		// The aggregator uses that distinction to report provenance coverage.
		originalEntryGroupId: r.originalEntryGroupId,
		originalMatterId: r.originalMatterId,
	};
}

export interface UseMetricsResult {
	summary: MetricsSummary | null;
	isLoading: boolean;
	error: string | null;
	/** Licensed timekeepers found, including those with no activity. */
	timekeeperCount: number;
}

export function useMetrics(startDate: Date, endDate: Date): UseMetricsResult {
	const { makeApiRequest } = useApi();
	const { professionals: allProfessionals, loading: profsLoading, error: profsError } =
		useProfessionals();

	// Dropped before the query, so excluded accounts are never fetched, never
	// aggregated, and never reachable through the table.
	const professionals = useMemo(
		() => allProfessionals.filter((p) => !isExcludedAccount(p.email)),
		[allProfessionals],
	);

	const start = toApiDate(startDate);
	const end = toApiDate(endDate);

	// Joined to a string so upstream reference changes don't refetch.
	const profIdsKey = useMemo(
		() =>
			professionals
				.map((p) => p.prof_id)
				.sort()
				.join(","),
		[professionals],
	);

	const query = useQuery({
		queryKey: ["internal", "metrics", profIdsKey, start, end] as const,
		enabled: profIdsKey.length > 0,
		queryFn: async ({ signal }) => {
			const params = new URLSearchParams({
				startDate: start,
				endDate: end,
				profIds: profIdsKey,
				includeDeleted: "true",
			});
			const res = await makeApiRequest({
				endpoint: `/admin/user-transactions?${params.toString()}`,
				method: "GET",
				otherOptions: { signal },
			});
			if (res.error) throw new Error(String(res.error));
			const data = res.data as {
				transactions: AdminUserTransaction[];
				summary?: { deleted_count?: number };
			};

			// The endpoint computes deleted_count server-side off the same literals
			// the rows carry. Cross-checking our row-derived count against it turns
			// a silent misclassification into a visible warning — this exact class
			// of bug reported a structural 0% deletion rate for weeks.
			const serverDeleted = data.summary?.deleted_count;
			if (typeof serverDeleted === "number") {
				const derived = data.transactions.filter(
					(r) => statusOf(r) === "deleted",
				).length;
				if (derived !== serverDeleted) {
					logger.warn(
						`[metrics] deleted-count mismatch: derived ${derived} from rows, server reported ${serverDeleted}. The synthesized 'source' literals may have changed.`,
					);
				}
			}

			return data.transactions;
		},
	});

	const summary = useMemo(() => {
		if (!query.data) return null;
		return aggregate({
			rows: query.data.map(toMetricsRow),
			timekeepers: professionals.map((p) => ({
				profId: p.prof_id,
				profName: p.full_name,
			})),
		});
	}, [query.data, professionals]);

	return {
		summary,
		isLoading: profsLoading || query.isLoading,
		error:
			profsError ??
			(query.error instanceof Error ? query.error.message : null),
		timekeeperCount: professionals.length,
	};
}
